/**
 * Bazzef Custom QR Code Utility
 *
 * Provides:
 * - Generation of branded Bazzef QR code matrices using 'qrcode'.
 * - Custom canvas rendering with smooth rounded dots, squircle corner eyes, and center avatar branding.
 * - Multiple signature Bazzef color themes.
 * - URL & deep link encoding/decoding for instant 1-tap messaging and following.
 */

import QRCode from 'qrcode';

export const BAZZEF_QR_THEMES = {
  classic: {
    id: 'classic',
    name: 'Bazzef Indigo',
    startColor: '#4f46e5', // indigo-600
    endColor: '#7c3aed',   // violet-600
    bg: '#ffffff',
    eyeColor: '#4338ca',
    cardBg: 'from-indigo-900/40 via-violet-900/30 to-black/60',
    accentBorder: 'border-indigo-500/40',
  },
  neon: {
    id: 'neon',
    name: 'Cyber Neon',
    startColor: '#06b6d4', // cyan-500
    endColor: '#a855f7',   // purple-500
    bg: '#ffffff',
    eyeColor: '#0891b2',
    cardBg: 'from-cyan-950/40 via-purple-950/30 to-black/60',
    accentBorder: 'border-cyan-500/40',
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Glow',
    startColor: '#f43f5e', // rose-500
    endColor: '#f59e0b',   // amber-500
    bg: '#ffffff',
    eyeColor: '#e11d48',
    cardBg: 'from-rose-950/40 via-amber-950/30 to-black/60',
    accentBorder: 'border-rose-500/40',
  },
  emerald: {
    id: 'emerald',
    name: 'Emerald Mint',
    startColor: '#10b981', // emerald-500
    endColor: '#0ea5e9',   // sky-500
    bg: '#ffffff',
    eyeColor: '#059669',
    cardBg: 'from-emerald-950/40 via-sky-950/30 to-black/60',
    accentBorder: 'border-emerald-500/40',
  },
  midnight: {
    id: 'midnight',
    name: 'Midnight Black',
    startColor: '#09090b', // zinc-950
    endColor: '#3f3f46',   // zinc-700
    bg: '#ffffff',
    eyeColor: '#18181b',
    cardBg: 'from-zinc-900/50 via-zinc-950/40 to-black/80',
    accentBorder: 'border-zinc-700/50',
  },
};

/**
 * Creates the universal URL encoded in a Bazzef user's QR code.
 * Scannable both natively with iPhone/Android camera and inside Bazzef.
 *
 * @param {object} user - User object
 * @returns {string} Fully qualified Bazzef connect URL
 */
export function buildBazzefQrUrl(user) {
  if (!user) return '';
  const username = user.username || user.handle || '';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://bazzef.com';
  return `${origin}/profile/${encodeURIComponent(username)}?bazzef_qr=1&uid=${encodeURIComponent(user.id || '')}`;
}

/**
 * Parses raw text from a scanned QR code to check if it represents a Bazzef user.
 * Supports universal URLs, deep links, JSON payloads, or username handles.
 *
 * @param {string} rawText
 * @returns {{ isBazzef: boolean, username: string, userId: string|null }}
 */
export function parseBazzefQrData(rawText) {
  if (!rawText || typeof rawText !== 'string') {
    return { isBazzef: false, username: '', userId: null };
  }

  const trimmed = rawText.trim();

  // 1. JSON payload
  try {
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      const parsed = JSON.parse(trimmed);
      if (parsed.bazzef || parsed.type === 'bazzef_user') {
        return {
          isBazzef: true,
          username: parsed.username || parsed.handle || '',
          userId: parsed.id || parsed.userId || null,
        };
      }
    }
  } catch (_) {}

  // 2. Universal web URL or deep link
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed);
      const isQrParam = url.searchParams.get('bazzef_qr') === '1';
      const uid = url.searchParams.get('uid');

      // Check /profile/:username or /u/:username
      const match = url.pathname.match(/^\/(?:profile|u)\/([^/?#]+)/i);
      if (match) {
        return {
          isBazzef: true,
          username: decodeURIComponent(match[1]),
          userId: uid || null,
        };
      }

      // Check /messages/:userId
      const msgMatch = url.pathname.match(/^\/messages\/([^/?#]+)/i);
      if (msgMatch) {
        return {
          isBazzef: true,
          username: '',
          userId: decodeURIComponent(msgMatch[1]),
        };
      }

      if (isQrParam) {
        return { isBazzef: true, username: '', userId: uid || null };
      }
    }

    // Deep link scheme: bazzef://user/:username
    if (/^bazzef:\/\//i.test(trimmed)) {
      const match = trimmed.match(/^bazzef:\/\/user\/([^/?#]+)/i);
      if (match) {
        return {
          isBazzef: true,
          username: decodeURIComponent(match[1]),
          userId: null,
        };
      }
    }
  } catch (_) {}

  // 3. Plain handle like @john or john (if scanned inside Bazzef)
  if (/^@?[a-zA-Z0-9_]{3,30}$/.test(trimmed)) {
    return {
      isBazzef: true,
      username: trimmed.replace(/^@/, ''),
      userId: null,
    };
  }

  return { isBazzef: false, username: '', userId: null };
}

/**
 * Checks if a module (row, col) belongs to one of the 3 corner finder eye patterns (7x7).
 */
function isFinderPattern(row, col, size) {
  if (row < 7 && col < 7) return true; // Top-Left
  if (row < 7 && col >= size - 7) return true; // Top-Right
  if (row >= size - 7 && col < 7) return true; // Bottom-Left
  return false;
}

/**
 * Checks if a module is in the center area reserved for the user avatar badge.
 */
function isCenterLogoArea(row, col, size) {
  const mid = Math.floor(size / 2);
  const radius = Math.floor(size * 0.18); // ~18% radius (well within 'H' 30% error correction)
  return Math.abs(row - mid) <= radius && Math.abs(col - mid) <= radius;
}

/**
 * Draws a rounded rectangle path on canvas.
 */
function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Draws a custom Bazzef position detection eye at (x, y) with size.
 */
function drawBazzefEye(ctx, x, y, size, gradient, bg = '#ffffff') {
  const outerRadius = size * 0.28;
  const innerRadius = size * 0.16;

  // Outer Squircle Border
  ctx.fillStyle = gradient;
  roundRect(ctx, x, y, size, size, outerRadius);
  ctx.fill();

  // Middle Background Cutout
  const strokeW = size * (1 / 7);
  const midX = x + strokeW;
  const midY = y + strokeW;
  const midSize = size - strokeW * 2;
  ctx.fillStyle = bg;
  roundRect(ctx, midX, midY, midSize, midSize, outerRadius * 0.7);
  ctx.fill();

  // Center Target Pip
  const pipOffset = size * (2 / 7);
  const pipSize = size * (3 / 7);
  ctx.fillStyle = gradient;
  roundRect(ctx, x + pipOffset, y + pipOffset, pipSize, pipSize, innerRadius);
  ctx.fill();
}

/**
 * Renders a stylized Bazzef QR code onto an HTML5 canvas.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {string} text - Payload to encode
 * @param {object} [options]
 * @param {string} [options.theme='classic'] - Key in BAZZEF_QR_THEMES
 * @param {number} [options.size=512] - Canvas dimension in px
 * @param {string|HTMLImageElement} [options.avatarImage] - Center avatar image element or src
 */
export async function renderBazzefQrCanvas(canvas, text, options = {}) {
  if (!canvas || !text) return;

  const width = options.size || 512;
  const height = width;
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const themeKey = options.theme && BAZZEF_QR_THEMES[options.theme] ? options.theme : 'classic';
  const theme = BAZZEF_QR_THEMES[themeKey];

  // Generate QR bit matrix with High error correction (30% redundancy)
  const qr = QRCode.create(text, {
    errorCorrectionLevel: 'H',
    version: undefined,
  });

  const matrixSize = qr.modules.size;
  const margin = Math.round(width * 0.08); // 8% quiet zone
  const innerSize = width - margin * 2;
  const cellSize = innerSize / matrixSize;

  // Clear background
  ctx.fillStyle = theme.bg;
  roundRect(ctx, 0, 0, width, height, width * 0.08);
  ctx.fill();

  // Create primary gradient for data modules and eyes
  const grad = ctx.createLinearGradient(margin, margin, width - margin, height - margin);
  grad.addColorStop(0, theme.startColor);
  grad.addColorStop(1, theme.endColor);

  // 1. Draw regular data modules as smooth rounded dots
  for (let r = 0; r < matrixSize; r++) {
    for (let c = 0; c < matrixSize; c++) {
      if (isFinderPattern(r, c, matrixSize)) continue;
      if (isCenterLogoArea(r, c, matrixSize)) continue;

      if (qr.modules.get(r, c)) {
        const cx = margin + c * cellSize + cellSize / 2;
        const cy = margin + r * cellSize + cellSize / 2;
        const radius = (cellSize * 0.88) / 2;

        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }
    }
  }

  // 2. Draw the 3 custom Bazzef corner eyes
  const eyePixelSize = cellSize * 7;
  // Top-Left Eye
  drawBazzefEye(ctx, margin, margin, eyePixelSize, grad, theme.bg);
  // Top-Right Eye
  drawBazzefEye(ctx, margin + (matrixSize - 7) * cellSize, margin, eyePixelSize, grad, theme.bg);
  // Bottom-Left Eye
  drawBazzefEye(ctx, margin, margin + (matrixSize - 7) * cellSize, eyePixelSize, grad, theme.bg);

  // 3. Draw Center Avatar / Logo Badge
  const centerRadiusModules = Math.floor(matrixSize * 0.18);
  const centerSize = (centerRadiusModules * 2 + 1) * cellSize;
  const centerX = width / 2;
  const centerY = height / 2;
  const avatarRadius = (centerSize * 0.94) / 2;

  // Background circle for badge to isolate from modules
  ctx.beginPath();
  ctx.arc(centerX, centerY, avatarRadius + cellSize * 0.4, 0, Math.PI * 2);
  ctx.fillStyle = theme.bg;
  ctx.fill();

  // Subtle outer gradient ring
  ctx.lineWidth = Math.max(2, Math.round(width * 0.012));
  ctx.strokeStyle = grad;
  ctx.stroke();

  // Draw Avatar image if provided
  let imgElement = options.avatarElement;
  if (!imgElement && options.avatarSrc) {
    imgElement = await loadImg(options.avatarSrc).catch(() => null);
  }

  if (imgElement) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, avatarRadius - 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(
      imgElement,
      centerX - avatarRadius,
      centerY - avatarRadius,
      avatarRadius * 2,
      avatarRadius * 2
    );
    ctx.restore();
  } else {
    // Fallback: draw Bazzef iconic stylized 'B' crest
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, avatarRadius - 2, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.round(avatarRadius * 1.1)}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('B', centerX, centerY + 1);
    ctx.restore();
  }
}

function loadImg(src) {
  return new Promise((resolve, reject) => {
    if (!src) return reject(new Error('No src'));
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
